import { ArrowLeft, User, Search, Plus, ChevronRight } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useBeneficiaries } from '@/hooks/useCpn'
import { CpnBeneficiaryUser } from '@/types/cpn'

interface SavedBeneficiariesScreenProps {
  onNext: (beneficiary?: CpnBeneficiaryUser) => void
  onBack: () => void
  onAddNew: () => void
  userId: string // We'll need to pass this from parent
}

const SavedBeneficiariesScreen = ({
  onNext,
  onBack,
  onAddNew,
  userId,
}: SavedBeneficiariesScreenProps) => {
  const [searchQuery, setSearchQuery] = useState('')

  // Fetch beneficiaries using our hook
  const {
    data: beneficiariesData,
    isLoading,
    error,
  } = useBeneficiaries(userId, { enabled: !!userId })

  const beneficiaries = beneficiariesData?.beneficiaryUsers || []

  // Filter beneficiaries based on search query
  const filteredBeneficiaries = beneficiaries.filter(
    (beneficiary) =>
      `${beneficiary.firstName} ${beneficiary.lastName}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      beneficiary.country?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleSelectBeneficiary = (beneficiary: CpnBeneficiaryUser) => {
    onNext(beneficiary)
  }

  return (
    <div className="h-[600px] flex flex-col">
      <div className="p-4 space-y-3 flex-1 flex flex-col">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-lg font-bold text-gray-900">Select recipient</h1>
          <p className="text-sm text-gray-600">
            Choose from your saved recipients
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search recipients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-10"
          />
        </div>

        {/* Add New Recipient Button */}
        <Button
          onClick={onAddNew}
          variant="outline"
          className="w-full flex items-center justify-center space-x-2 h-10 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add new recipient</span>
        </Button>

        {/* Beneficiaries List */}
        <div className="flex-1 space-y-2 overflow-y-auto">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="p-3 animate-pulse">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                    <div className="flex-1 space-y-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : error ? (
            <Card className="p-4 text-center">
              <p className="text-sm text-red-600">Failed to load recipients</p>
              <p className="text-xs text-gray-500 mt-1">Please try again</p>
            </Card>
          ) : filteredBeneficiaries.length === 0 ? (
            <Card className="p-4 text-center">
              {beneficiaries.length === 0 ? (
                <>
                  <User className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    No saved recipients yet
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Add your first recipient to get started
                  </p>
                </>
              ) : (
                <>
                  <Search className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    No recipients match your search
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Try a different search term
                  </p>
                </>
              )}
            </Card>
          ) : (
            filteredBeneficiaries.map((Recipient) => (
              <Card
                key={Recipient.uuid}
                className="p-3 cursor-pointer hover:shadow-md transition-shadow border-gray-200"
                onClick={() => handleSelectBeneficiary(Recipient)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium text-sm text-gray-900">
                        {Recipient.firstName} {Recipient.lastName}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {Recipient.city && Recipient.country
                          ? `${Recipient.city}, ${Recipient.country}`
                          : Recipient.country || 'No location set'}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Back Button */}
        <div className="pt-1">
          <Button
            variant="outline"
            onClick={onBack}
            className="w-full flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border border-gray-300 hover:bg-yellow-50 hover:border-yellow-200 transition-colors h-10"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Button>
        </div>
      </div>

      {/* Powered by Paytrie */}
      <div className="text-center p-2 border-t border-gray-100">
        <p className="text-xs text-gray-500 mb-1">Powered by Paytrie</p>
        <img
          src="/paytrie-logo.png"
          alt="Paytrie"
          className="h-6 w-auto mx-auto"
        />
      </div>
    </div>
  )
}

export default SavedBeneficiariesScreen
